<template>
  <div>
    <div v-if="loading" class="text-center py-5"><b-spinner variant="primary" /></div>

    <template v-else>
      <!-- ── KPIs ── -->
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="row" style="row-gap:18px;">
            <div v-for="k in kpis" :key="k.label" class="col-6 col-md-3">
              <div class="text-secondary mb-1 small">{{ k.label }}</div>
              <div class="d-flex align-items-center flex-wrap" style="gap:8px;">
                <span class="h5 font-weight-bold text-dark mb-0">{{ k.value }}</span>
                <span v-if="k.badge" class="badge badge-pill" :class="k.badgeClass || 'badge-light text-secondary'">{{ k.badge }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- ── LAPSED REGULARS ── -->
        <div class="col-12 col-lg-7 mb-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-header bg-white d-flex flex-wrap justify-content-between align-items-center">
              <div class="mr-2">
                <div class="font-weight-bold text-dark">Members who stopped coming</div>
                <small class="text-secondary">
                  Active members with {{ settings.regular_min_visits }}+ visits, not seen in {{ settings.inactivity_days }}+ days
                </small>
              </div>
              <b-form-checkbox
                v-if="canEdit"
                v-model="settings.inactivity_alerts_enabled"
                switch
                class="mt-2 mt-md-0"
                @change="save({ inactivity_alerts_enabled: $event })"
              >
                Auto-send "we miss you" SMS
              </b-form-checkbox>
            </div>
            <div class="card-body p-0">
              <div v-if="loadingInactive" class="text-center py-4"><b-spinner small /></div>
              <div v-else-if="!inactive.length" class="text-center text-secondary py-4">
                <i class="bi bi-emoji-smile d-block mb-1" style="font-size:1.5rem;"></i>
                Everyone has been in recently.
              </div>
              <ul v-else class="list-group list-group-flush">
                <li v-for="m in inactive" :key="m.subscription_id" class="list-group-item d-flex flex-wrap justify-content-between align-items-center">
                  <div class="mr-2">
                    <div class="font-weight-bold text-dark">{{ m.name }}</div>
                    <small class="text-secondary">
                      {{ m.package }} · {{ m.visits }} visits · last {{ fromNow(m.last_visit) }}
                      <template v-if="m.last_nudged_at"> · reminded {{ fromNow(m.last_nudged_at) }}</template>
                    </small>
                  </div>
                  <div class="d-flex align-items-center mt-1 mt-md-0">
                    <span class="badge badge-danger mr-2">{{ m.days_away }} days</span>
                    <a v-if="m.phone" :href="'tel:' + m.phone" class="btn btn-sm btn-outline-secondary mr-1" title="Call">
                      <i class="bi bi-telephone"></i>
                    </a>
                    <button
                      v-if="can('subscriptions.edit')"
                      class="btn btn-sm btn-outline-primary"
                      :disabled="nudging === m.subscription_id"
                      @click="nudge(m)"
                    >
                      <b-spinner v-if="nudging === m.subscription_id" small />
                      <template v-else><i class="bi bi-chat-text mr-1"></i>Remind</template>
                    </button>
                  </div>
                </li>
              </ul>
            </div>
            <div v-if="canEdit" class="card-footer bg-white d-flex flex-wrap align-items-center small text-secondary">
              <span class="mr-2">Not seen for</span>
              <b-form-select
                v-model="settings.inactivity_days"
                :options="[7, 10, 14, 21, 30, 45, 60]"
                class="w-auto mr-2"
                @change="save({ inactivity_days: $event }, true)"
              />
              <span class="mr-2">days, with at least</span>
              <b-form-select
                v-model="settings.regular_min_visits"
                :options="[1, 2, 3, 5, 8, 10]"
                class="w-auto mr-2"
                @change="save({ regular_min_visits: $event }, true)"
              />
              <span>visits</span>
            </div>
          </div>
        </div>

        <!-- ── AUTOMATED SMS + MEMBER APP ── -->
        <div class="col-12 col-lg-5 mb-3">
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-white font-weight-bold text-dark">SMS this month</div>
            <div class="card-body">
              <div class="d-flex justify-content-between mb-1">
                <span class="text-secondary">{{ sms.used }} of {{ sms.free }} free</span>
                <span v-if="sms.over" class="text-danger font-weight-bold">{{ sms.over }} extra · KES {{ sms.over * sms.unit_price }}</span>
              </div>
              <b-progress :max="sms.free" height="8px">
                <b-progress-bar :value="Math.min(sms.used, sms.free)" :variant="sms.used >= sms.free ? 'danger' : 'primary'" />
              </b-progress>
              <small class="text-secondary d-block mt-2">
                The first {{ sms.free }} SMS each month are free, then KES {{ sms.unit_price }} each. You'll be notified at 80 and 100.
              </small>
            </div>
          </div>

          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white">
              <div class="font-weight-bold text-dark">Member app & automated SMS</div>
              <small class="text-secondary">All off by default - turn on what you want.</small>
            </div>
            <ul class="list-group list-group-flush">
              <li v-for="t in toggles" :key="t.key" class="list-group-item">
                <b-form-checkbox
                  v-model="settings[t.key]"
                  switch
                  :disabled="!canEdit"
                  @change="save({ [t.key]: $event })"
                >
                  <span class="text-dark">{{ t.label }}</span>
                </b-form-checkbox>
                <small class="text-secondary d-block ml-4">{{ t.help }}</small>
                <div v-if="t.key === 'expiry_reminders_enabled' && settings.expiry_reminders_enabled" class="ml-4 mt-2">
                  <small class="text-secondary d-block mb-1">Send reminders this many days before expiry:</small>
                  <b-form-checkbox-group
                    v-model="settings.reminder_days"
                    :options="[14, 7, 3, 1]"
                    :disabled="!canEdit"
                    @change="save({ reminder_days: $event }, true)"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- ── REVENUE BY PACKAGE ── -->
      <div v-if="financials && financials.by_package && financials.by_package.length" class="card border-0 shadow-sm mb-3">
        <div class="card-header bg-white font-weight-bold text-dark">Revenue by package (this month)</div>
        <ul class="list-group list-group-flush">
          <li v-for="row in financials.by_package" :key="row.package" class="list-group-item">
            <div class="d-flex justify-content-between mb-1">
              <span class="text-dark">{{ row.package }}</span>
              <span class="font-weight-bold text-dark">KES {{ fmt(row.revenue) }}</span>
            </div>
            <b-progress :max="maxPackageRevenue" height="4px">
              <b-progress-bar :value="row.revenue" variant="primary" />
            </b-progress>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'

/** Overview tab on the Subscriptions page: KPIs, lapsed regulars, SMS usage and member-journey settings. */
export default {
  name: 'SubscriptionOverview',

  data() {
    return {
      loading:         true,
      loadingInactive: false,
      dashboard:       {},
      financials:      null,
      settings:        { inactivity_days: 14, regular_min_visits: 3, reminder_days: [7, 3, 1] },
      canEdit:         false,
      sms:             { used: 0, free: 100, over: 0, unit_price: 1 },
      inactive:        [],
      nudging:         null,
    }
  },

  computed: {
    kpis() {
      const d = this.dashboard
      const c = d.counts || {}
      const g = d.growth || {}
      const a = d.attendance || {}
      const e = d.expiring_soon || {}
      const list = [
        { label: 'Active members',     value: c.active || 0, badge: c.past_due ? `${c.past_due} past due` : null, badgeClass: 'badge-danger' },
        { label: 'New this month',     value: g.new_members_this_month || 0 },
        { label: 'Renewals this month', value: g.renewals_this_month || 0 },
        { label: 'Expiring in 7 days', value: e.in_7_days || 0, badge: e.in_30_days ? `${e.in_30_days} in 30d` : null },
        { label: 'Inside now',         value: a.inside_now || 0, badge: `${a.today_entries || 0} today` },
        { label: 'Visits (30 days)',   value: g.visits_30d || 0, badge: g.unique_visitors_30d ? `${g.unique_visitors_30d} people` : null },
      ]
      if (this.financials) {
        const f = this.financials
        const change = f.revenue_last_month > 0
          ? Math.round(((f.revenue_this_month - f.revenue_last_month) / f.revenue_last_month) * 100)
          : null
        list.push({
          label: 'Revenue this month',
          value: 'KES ' + this.fmt(f.revenue_this_month),
          badge: change === null ? null : `${change >= 0 ? '↑' : '↓'} ${Math.abs(change)}%`,
          badgeClass: change !== null && change < 0 ? 'badge-danger' : 'badge-primary',
        })
        list.push({
          label: 'Outstanding',
          value: 'KES ' + this.fmt(f.outstanding_balance),
          badgeClass: 'badge-danger',
        })
      }
      return list
    },

    toggles() {
      return [
        { key: 'expiry_reminders_enabled', label: 'Expiry reminders',          help: 'SMS with a one-tap renew link before a membership ends. Skips daily/short passes.' },
        { key: 'welcome_sms_enabled',      label: 'Welcome SMS',               help: "Sent on a member's first subscription, with their pass and app link." },
        { key: 'receipt_sms_enabled',      label: 'Renewal & cancellation SMS', help: 'Confirms renewals (desk, online, auto) and cancellations.' },
        { key: 'require_otp_first_visit',  label: 'Verify phone on first scan', help: 'Members confirm a 4-digit SMS code once per phone, so nobody can check in on another number.' },
      ]
    },

    maxPackageRevenue() {
      return Math.max(1, ...((this.financials && this.financials.by_package) || []).map(r => r.revenue))
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        const [dash, settings] = await Promise.all([
          axios.get('/api/subscriptions/dashboard'),
          axios.get('/api/subscriptions/member-settings'),
        ])
        this.dashboard  = dash.data
        this.financials = dash.data.financials || null
        this.settings   = { ...this.settings, ...settings.data.settings, reminder_days: settings.data.settings.reminder_days || [7, 3, 1] }
        this.canEdit    = settings.data.can_edit
        this.sms        = settings.data.sms_usage
      } catch (e) {
        this.$bvToast.toast('Could not load the overview.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.loading = false
      }
      this.loadInactive()
    },

    async loadInactive() {
      this.loadingInactive = true
      try {
        const { data } = await axios.get('/api/subscriptions/inactive-members', { _silent: true })
        this.inactive = data.members
      } catch (e) {
        this.inactive = []
      } finally {
        this.loadingInactive = false
      }
    },

    async save(patch, reloadInactive = false) {
      try {
        const { data } = await axios.put('/api/subscriptions/member-settings', patch, { _silent: true })
        this.settings = { ...this.settings, ...data.settings }
        this.$bvToast.toast('Saved', { variant: 'primary', solid: true, autoHideDelay: 1500, noCloseButton: true })
        if (reloadInactive) this.loadInactive()
      } catch (e) {
        this.$bvToast.toast(e.response?.data?.message || 'Could not save.', { title: 'Error', variant: 'danger', solid: true })
        this.load()
      }
    },

    async nudge(m) {
      this.nudging = m.subscription_id
      try {
        await axios.post(`/api/subscriptions/inactive-members/${m.subscription_id}/nudge`, {}, { _silent: true })
        m.last_nudged_at = new Date().toISOString()
        this.$bvToast.toast(`Reminder sent to ${m.name}`, { variant: 'primary', solid: true, autoHideDelay: 2000 })
      } catch (e) {
        this.$bvToast.toast('SMS could not be sent.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.nudging = null
      }
    },

    fromNow(d) { return moment(d).fromNow() },

    fmt(v) {
      return parseFloat(v || 0).toLocaleString('en-KE', { maximumFractionDigits: 0 })
    },
  },
}
</script>
